import React from 'react';
import { aeroAccent } from './glassEngine';

/**
 * GlassTag — a status tag/badge with color.
 * These are NOT buttons. They serve as visual indicators AND as
 * data-color sources for the glass engine (nearby buttons pick up their color).
 *
 * Props:
 *   color    — hex color (e.g. '#3D78B0'). Used for text, tint, AND data-color source.
 *              Defaults to the resolved --aero-accent token. Must be a 6-digit hex
 *              because it is parsed into r/g/b for the tint and the glass engine.
 *   variant  — 'default' (light bg) | 'dark' (dark bg) | 'solid' (filled)
 *   size     — 'sm' | 'md' (default)
 *   children — label text
 *   className — pass through
 */
export default function GlassTag({
  color,
  variant = 'default',
  size = 'md',
  children,
  className = '',
  ...rest
}) {
  const resolvedColor = color || aeroAccent();
  // Compute rgba tint from hex
  const r = parseInt(resolvedColor.slice(1, 3), 16);
  const g = parseInt(resolvedColor.slice(3, 5), 16);
  const b = parseInt(resolvedColor.slice(5, 7), 16);

  const styles = {
    default: {
      background: `rgba(${r},${g},${b},0.1)`,
      color: resolvedColor,
      border: `1px solid rgba(${r},${g},${b},0.08)`,
    },
    dark: {
      background: `rgba(${r},${g},${b},0.2)`,
      color: `rgba(${r + 60 > 255 ? 255 : r + 60},${g + 60 > 255 ? 255 : g + 60},${b + 60 > 255 ? 255 : b + 60},0.9)`,
      border: `1px solid rgba(${r},${g},${b},0.15)`,
    },
    solid: {
      background: resolvedColor,
      color: 'var(--aero-accent-text)',
      border: `1px solid transparent`,
    },
  };

  const sizeClass = size === 'sm' ? 'aero-tag-sm' : '';

  return (
    <span
      className={`aero-tag ${sizeClass} ${className}`.trim()}
      style={styles[variant] || styles.default}
      data-color={resolvedColor}
      {...rest}
    >
      {children}
    </span>
  );
}
